import { getSessionStorage, setSessionStorage } from '@tcp/core/src/utils';
import { md5 } from '@tcp/core/src/utils/encoding';
import {
  getProductDetailNClickJSON,
  getProductImpressionJSON,
  getRemovedProductJSON,
  getProductCartViewJSON,
  getAddToWishListJSON,
  getRemoveFromWishlistJSON,
  getBagPageEditClickJSON,
  getProductSaveForLaterJSON,
  getCrossSellClicksJSON,
  getProductPurchaseConfirmationJSON,
  getProductQuickViewJSON,
  getProductCheckoutJSON,
  getPaymentBillingJson,
  getCheckoutBillingJson,
  getCheckoutStartJson,
  getBagPageBillingJson,
  getCheckoutProducts,
} from '@tcp/core/src/adobeAnalytics/adobeData.products.mapper';

const sortMapper = {
  'min_offer_price desc': 'Price: High to Low',
  'min_offer_price asc': 'Price: Low to High',
  'newest_score desc': 'Newest',
  'favoritedcount desc': 'Most favorited',
  'ratings desc': 'Top Rated',
};

export const getSortParams = (value) => {
  const sort = value;
  if (sort) {
    if (sortMapper[sort]) {
      return `${sortMapper[sort]}`;
    }
    return `top rated`;
  }
  return 'recommended';
};

function getPreviousPageName(data) {
  const sessionVal = getSessionStorage('previousPageName') || '';
  setSessionStorage({ key: 'previousPageName', value: data?.pageData.pageName });
  return sessionVal ? `${data?.APIConfig?.brandId?.toLowerCase()}:${sessionVal}` : '';
}

function getPreviousPageURL() {
  const sessionVal = getSessionStorage('previousPageURL') || '';
  setSessionStorage({ key: 'previousPageURL', value: window?.location?.href });
  return sessionVal;
}

export function getBreadCrumbs(data, isPageView) {
  let breadCrumbs;
  if (data?.pageData?.pageSection) {
    if (isPageView) {
      breadCrumbs =
        data?.pageData?.pageName !== 'home' ? `home:${data?.pageData?.pageSection}` : 'home';
    } else {
      breadCrumbs = data?.pageData?.pageSection;
    }
  }
  if (data?.pageData?.pageSubSection) {
    breadCrumbs += `-${data?.pageData?.pageSubSection}`;
  }
  if (data?.pageData?.pageSubSection2) {
    breadCrumbs += `-${data?.pageData?.pageSubSection2}`;
  }
  return breadCrumbs;
}

function getHpVersion(data) {
  return data?.APIConfig?.regionCode || 'default';
}

export function getGlobalPageLoadEventJson(data) {
  const userInfo = getSessionStorage('user_info');
  return {
    pageInfo: {
      pageName: `${data?.APIConfig?.brandId || ''}:${data?.pageData?.pageName || ''}`,
      pageType: data?.pageData?.pageType,
      pageURLFull: window?.location?.href,
      pageURL: `https://${document?.location?.hostname}${document?.location?.pathname}`,
      previousPageName: getPreviousPageName(data),
      previousPageURL: getPreviousPageURL(),
      siteSection: data?.pageData?.pageSection ? data?.pageData?.pageSection : '',
      pageSubSection: data?.pageData?.pageSubSection ? data?.pageData?.pageSubSection : '',
      pageSubSection2: data?.pageData?.pageSubSection2 ? data?.pageData?.pageSubSection2 : '',
      pageLocale: `${
        data?.session?.language.toLowerCase() || data?.APIConfig?.language.toLowerCase() || ''
      }:${data?.session?.country.toLowerCase() || data?.APIConfig?.country.toLowerCase() || ''}`,
      browserMode: 'normal',
      pageBreadCrumbs: getBreadCrumbs(data, true) || '',
      hpVersion160: getHpVersion(data),
    },
    userInfo: {
      emailID: JSON.parse(userInfo)?.email
        ? md5(JSON.parse(userInfo)?.email.toLowerCase()).toUpperCase()
        : '',
      customerType:
        userInfo && JSON.parse(userInfo)?.email ? 'no rewards:logged in' : 'no rewards:guest',
      customerID: JSON.parse(userInfo)?.userID,
      loginStatus: userInfo && JSON.parse(userInfo)?.email ? 'Logged In' : 'Guest',
      countryID: data?.APIConfig?.storeId || '',
    },
    brandInfo: {
      brandName: data?.APIConfig?.brandId?.toUpperCase(),
      brandApplication: `${data?.APIConfig?.brandId?.toUpperCase()} Web`,
    },
  };
}

export function getAccountSuccessEventJSON(data, isLogin) {
  return {
    event: isLogin ? 'login-success' : 'registration-success',
    eventinfo: {
      eventName: isLogin ? 'Login Success' : 'Registration Success',
      loginPath: data?.loginPath,
      zipCode: data?.zipCode,
    },
    userInfo: {
      emailID: data?.userEmailId ? md5(data?.userEmailId.toLowerCase()).toUpperCase() : null,
      customerType: data?.loggedInState ? 'no rewards:logged in' : 'no rewards:guest',
      customerID: data?.customerID || '',
    },
  };
}

export function getAccountFailJson(data, isLogin) {
  return {
    event: isLogin ? 'login-failure' : 'registration-failure',
    eventinfo: {
      eventName: isLogin ? 'Login Failed' : 'Registration Failed',
      validationErrorCode: data?.errForAA || data,
      ...(isLogin && {
        loginPath: data?.loginPath,
      }),
    },
  };
}
export function getHeaderNFooterJson(data, isHeader) {
  return {
    event: isHeader ? 'header-click' : 'footer-click',
    eventinfo: {
      eventName: isHeader ? 'Header Click' : 'Footer Click',
      navClickPath: data,
    },
  };
}

export function getShowHideFiltersJSON({ hideFilters }) {
  return {
    event: 'show-hide-filters-click',
    eventinfo: {
      eventName: 'Show Hide Filters Click',
      showHideFilterData: hideFilters ? 'show filters' : 'hide filters',
    },
  };
}

export function getStyliticsShoppableImageClick(data) {
  return {
    event: 'stylitics-shoppable-image-click',
    eventinfo: {
      eventName: 'Stylitics shoppable image click',
      shoppableImage151: data,
    },
  };
}

const getSortForAA = (data) => {
  return data?.SortForAA ? `^${data.SortForAA}` : '';
};

export function getSortNFilterJson(data, isSort) {
  const eventMethod = isSort ? 'sortMethod' : 'filtersSelected';
  const eventInfo = isSort ? 'sortInfo' : 'filterInfo';
  return {
    event: isSort ? 'sort' : 'filters',
    [eventInfo]: {
      eventName: isSort ? 'Sort By' : 'Filter By',
      [eventMethod]: isSort
        ? `sort=${(getSortParams(data?.sortSelected) || '').concat(getSortForAA(data))}`
        : data,
    },
  };
}

export function getInternalSearchJson(data, isInternalSearch) {
  const eventMethod = isInternalSearch ? 'searchType' : 'didYouMeanSearchTerm';
  return {
    event: isInternalSearch ? 'internal-search' : 'internal-search-error',
    searchInfo: {
      eventName: isInternalSearch ? 'Internal Search' : 'Did You Mean Search',
      searchTerm: data?.searchedText || data?.searchKeywordForAA,
      [eventMethod]: isInternalSearch ? data?.searchType || data?.searchTypeForAA : data?.wrongTerm,
      searchResultsCount: data?.totalProductsCount || data?.searchResultsForAA,
    },
  };
}

export function getBannerImpressionJSON(data) {
  const newStore = data?.map((item) => {
    return {
      internalCampaignID: item?.id,
    };
  });
  return {
    event: 'internal-campaign-impression',
    eventinfo: {
      eventName: 'Internal Campaign Impression',
      internalCampaignList: JSON.stringify(newStore),
    },
  };
}

export function getPhoneNEmailSubscription(isPhone) {
  return {
    event: isPhone ? 'phone-subscription' : 'email-subscription',
    eventinfo: {
      eventName: isPhone ? 'Phone Subscription' : 'Email Subscription',
    },
  };
}

export function getCheckoutPickUpBillingShippingNReview(value, data) {
  let event = '';
  let eventName = '';
  const { productsData } = data;
  const productListItems = JSON.stringify(getCheckoutProducts(productsData));
  switch (value) {
    case 'billing':
      event = 'checkout-billing';
      eventName = 'Checkout Billing';
      break;
    case 'shipping':
      event = 'checkout-shipping';
      eventName = 'Checkout Shipping';
      break;
    case 'review':
      event = 'checkout-review';
      eventName = 'Checkout Review';
      break;
    case 'pickup':
      event = 'checkout-pickup';
      eventName = 'Checkout Pickup';
      break;
    default:
  }
  return {
    event,
    checkoutInfo: {
      eventName,
      productListItems,
    },
  };
}

export const getAddToCartJson = (payload) => {
  const { prevCartCount, data } = payload;
  return {
    event: 'add-to-cart',
    productInfo: {
      eventName: prevCartCount === 0 ? 'Add To Cart, Cart Open' : 'Add To Cart',
      productAddToCartList: JSON.stringify(data),
    },
  };
};

export function getCouponSuccessJSON(data) {
  return {
    event: 'coupon-success',
    eventinfo: {
      eventName: 'Coupon Success',
      couponCode: data,
    },
  };
}

export function getCouponFailedJSON(data) {
  return {
    event: 'coupon-failed',
    eventinfo: {
      eventName: 'Coupon Failed',
      couponCode: data?.coupon,
      validationErrorCode: data?.ValidationErrorCode,
    },
  };
}

export function getCouponCodeRemovedJSON(data) {
  return {
    event: 'remove-coupon-code',
    eventinfo: {
      eventName: 'Remove Coupon Code',
      couponCode: data,
    },
  };
}

export function getLoginCheckoutEmailNPhoneJSON(isEmail) {
  return {
    event: isEmail ? 'checkout-emailentry' : 'checkout-passwordentry',
    checkoutInfo: {
      eventName: isEmail ? 'Checkout Email Entry' : 'Checkout Password Entry',
    },
  };
}

export function getSelfHelpOrderNRefundJSON(data, isCancel) {
  return {
    event: isCancel ? 'Self-Chat-Refund amount' : 'order-of-selfhelp',
    eventinfo: {
      eventName: isCancel ? 'SelfChatRefundAmount' : 'Order Of Self Help',
      orderID: isCancel ? data?.orderDetailsData?.orderNumber : data,
      ...(isCancel && {
        refundAmount: data?.refundTotal,
      }),
    },
  };
}

export function getPlccSubmitNNoThanksJSON(isSubmit) {
  return {
    event: isSubmit ? 'plcc-form-submit' : 'plcc-nothanks',
    eventinfo: {
      eventName: isSubmit ? 'PLCC Form Submit' : 'PLCC-No thanks',
    },
  };
}
export function getReviewSubmissionJSON(data, isReview) {
  return {
    event: isReview ? 'review-submission' : 'rating-submission',
    eventinfo: {
      eventName: isReview ? 'Review submission' : 'Rating Submission',
      reviewAndRatingLocation: data,
    },
  };
}
export function getMergeAccountYesJSON(isMerge) {
  return {
    event: isMerge ? 'merge-account-yes' : 'merge-account-cancel',
    eventinfo: {
      eventName: isMerge ? 'Merge Account - Yes' : 'Merge Account Cancel',
    },
  };
}
export function getLocationOfSelfHelpJSON(data) {
  return {
    event: 'location-of-selfhelp',
    eventinfo: {
      eventName: 'Location Of SelfHelp',
      locationOfSelfHelp: data,
    },
  };
}
export function getOtpGenerateSuccessJSON(data) {
  return {
    event: 'otp-generate-success',
    eventinfo: {
      eventName: 'Otp Generated Successfully',
      loginPath: data?.login_path_cd123,
    },
  };
}
export function getLiveChatJSON() {
  return {
    event: 'live-chat-opens',
    eventinfo: {
      eventName: 'Live Chat Opens',
    },
  };
}
export function getMergeAccSuccessnFailedJSON(isMerged) {
  return {
    event: isMerged ? 'merge-account-success' : 'merge-account-failure',
    eventinfo: {
      eventName: isMerged ? 'Merge Account - Success' : 'Merge Account - Failure',
    },
  };
}

export function getPriceDropShownJSON() {
  return {
    event: 'Price_Drop_Shown_v145',
    eventinfo: {
      eventName: 'Price_Drop_Shown_v145',
      value: 'yes',
    },
  };
}

export function getValidationErrorJSON(data) {
  return {
    event: 'error',
    eventinfo: {
      eventName: 'Error Validation',
      validationErrorCode: data,
    },
  };
}

export function getInternalCampaignClickJSON(data) {
  return {
    event: 'internal-campaign-click',
    eventinfo: {
      eventName: 'Internal Campaign Click',
      bannerName: data,
      internalCampaignID: data,
    },
  };
}
export function getSelfHelpPathJSON(data) {
  return {
    event: 'self-help-path',
    eventinfo: {
      eventName: 'self help path',
      selfHelpPath: data,
    },
  };
}

export function bagEditBtnClick() {
  return {
    event: 'bag-page-edit-button-clicks',
    eventinfo: {
      eventName: 'bag page edit button clicks',
    },
  };
}

export function shareCartClick() {
  return {
    event: 'share-cart-button-click',
    eventinfo: {
      eventName: 'share cart button click',
      buttonName: 'share cart|bag page',
    },
  };
}

export function referAFriendClick() {
  return {
    event: 'refer-a-friend-click',
    eventinfo: {
      eventName: 'refer a friend click',
      buttonName: 'refer a friend',
    },
  };
}

export function bossUnavilableViewBagClick() {
  return {
    event: 'boss-unavilable-view-bag-click',
    eventinfo: {
      eventName: 'boss unavilable view bag click',
      buttonName: 'view bag',
    },
  };
}

export function productImpressionPageView(data) {
  const productImpression = getProductImpressionJSON(data);
  const pageInfo = getGlobalPageLoadEventJson(data?.pageView);
  return {
    ...productImpression,
    ...pageInfo,
  };
}

export function pjplannerInteractionClick(data) {
  const { buttonName, pjplannerClickValue } = data;
  return {
    event: 'pjplanner-interaction-click',
    eventinfo: {
      eventName: 'PJPlanner interaction click',
      button: buttonName,
      pjplannerClickValue,
    },
  };
}

export function getRoktPlacementType(data) {
  return {
    event: 'show-rokt-offers',
    eventinfo: {
      eventName: 'Show Rokt Offers',
      placementType: data?.placementType || '',
    },
  };
}

function getAdobeAnalyticsData(eventType, data) {
  const valueMap = {
    page_view: () => getGlobalPageLoadEventJson(data),
    login_success: () => getAccountSuccessEventJSON(data, true),
    login_fail: () => getAccountFailJson(data, true),
    registration_success: () => getAccountSuccessEventJSON(data),
    registration_fail: () => getAccountFailJson(data),
    footer_click: () => getHeaderNFooterJson(data),
    header_click: () => getHeaderNFooterJson(data, true),
    apply_sort: () => getSortNFilterJson(data, true),
    apply_filter: () => getSortNFilterJson(data),
    internal_search: () => getInternalSearchJson(data, true),
    internal_search_error: () => getInternalSearchJson(data),
    add_to_cart: () => getAddToCartJson(data),
    product_impression: () => productImpressionPageView(data),
    product_click: () => getProductDetailNClickJSON(data),
    product_detail: () => getProductDetailNClickJSON(data, true),
    phone_subscription: () => getPhoneNEmailSubscription(true),
    email_subscription: () => getPhoneNEmailSubscription(),
    checkout_billing: () => getCheckoutPickUpBillingShippingNReview('billing', data),
    checkout_shipping: () => getCheckoutPickUpBillingShippingNReview('shipping', data),
    checkout_review: () => getCheckoutPickUpBillingShippingNReview('review', data),
    checkout_pickup: () => getCheckoutPickUpBillingShippingNReview('pickup', data),
    promotional_banner_impression: () => getBannerImpressionJSON(data),
    remove_from_cart: () => getRemovedProductJSON(data),
    product_cart_view: () => getProductCartViewJSON(data),
    coupon_success: () => getCouponSuccessJSON(data),
    coupon_failed: () => getCouponFailedJSON(data),
    remove_coupon_code: () => getCouponCodeRemovedJSON(data),
    checkout_email: () => getLoginCheckoutEmailNPhoneJSON(true),
    csh_refund: () => getSelfHelpOrderNRefundJSON(data, true),
    order_of_self_help: () => getSelfHelpOrderNRefundJSON(data),
    plcc_submit: () => getPlccSubmitNNoThanksJSON(true),
    plcc_no_thanks: () => getPlccSubmitNNoThanksJSON(),
    add_to_wishlist: () => getAddToWishListJSON(data),
    remove_from_wishlist: () => getRemoveFromWishlistJSON(data),
    review_submission: () => getReviewSubmissionJSON(data, true),
    rating_submission: () => getReviewSubmissionJSON(data, false),
    bag_page_edit_click: () => getBagPageEditClickJSON(data),
    merge_acc_yes: () => getMergeAccountYesJSON(true),
    merge_acc_cancel: () => getMergeAccountYesJSON(),
    save_for_later: () => getProductSaveForLaterJSON(data, true),
    move_to_bag: () => getProductSaveForLaterJSON(data),
    cross_sell_clicks: () => getCrossSellClicksJSON(data),
    purchase: () => getProductPurchaseConfirmationJSON(data),
    purchase_1: () => getProductPurchaseConfirmationJSON(data, true),
    location_of_self_help: () => getLocationOfSelfHelpJSON(data),
    product_quick_view: () => getProductQuickViewJSON(data),
    product_checkout: () => getProductCheckoutJSON(data),
    otp_generate_success: () => getOtpGenerateSuccessJSON(data),
    payment_billing_initiate: () => getPaymentBillingJson(data),
    checkout_start: () => getCheckoutStartJson(data),
    checkout_page_billing_start: () => getCheckoutBillingJson(data),
    bag_page_billing_start: () => getBagPageBillingJson(data),
    live_chat: () => getLiveChatJSON(),
    merge_acc_success: () => getMergeAccSuccessnFailedJSON(true),
    merge_acc_failure: () => getMergeAccSuccessnFailedJSON(),
    registration_fail_inline: () => getAccountFailJson(data),
    form_error: () => getValidationErrorJSON(data),
    internal_campaign_click: () => getInternalCampaignClickJSON(data),
    self_help_path: () => getSelfHelpPathJSON(data),
    bag_edit_btn_click: () => bagEditBtnClick(),
    share_cart_click: () => shareCartClick(),
    refer_a_friend_click: () => referAFriendClick(),
    boss_unavailable_button_click: () => bossUnavilableViewBagClick(),
    price_drop_shown: () => getPriceDropShownJSON(),
    pjplanner_interaction_click: () => pjplannerInteractionClick(data),
    default: () => '',
    stylitics_shoppable_image_click: () => getStyliticsShoppableImageClick(data),
    show_hide_filters_click: () => getShowHideFiltersJSON(data),
    show_rokt_offers: () => getRoktPlacementType(data),
  };
  const getValue = valueMap[eventType] || valueMap.default;
  return getValue();
}

export default getAdobeAnalyticsData;
